import React, { useEffect } from "react";
import { Navigate, Link } from "react-router-dom";
import { Phone, MapPin, CheckCircle, ArrowRight, ArrowLeft } from "lucide-react";
import { Button } from "../components/ui/button";
import SEO from "../components/SEO";
import StarRating from "../components/StarRating";
import ResponsiveImage from "../components/ResponsiveImage";
import Services from "../components/Services";
import CalculatorWidget from "../components/CalculatorWidget";
import Reviews from "../components/Reviews";
import FAQ from "../components/FAQ";
import Contact from "../components/Contact";
import { companyInfo } from "../data/mock";
import { images } from "../data/images";
import { getLocationBySlug } from "../data/locations";

const SITE_URL = "https://www.clevencleaning.nl";

export default function LocationPage({ slug }) {
  const location = getLocationBySlug(slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!location) {
    return <Navigate to="/" replace />;
  }

  const url = `${SITE_URL}/glazenwasser-${location.slug}`;
  const title = `${location.title} | Cleven Cleaning - 4.8★ op Google`;
  const description = `Glazenwasser in ${location.name} nodig? Cleven Cleaning verzorgt glasbewassing, kozijn- en zonnepaneelreiniging in ${location.name} en omstreken. Vaste glazenwasser, vooraf bericht. Bel 06-83653511 of bereken direct uw prijs.`;

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Service",
        "@id": `${url}#service`,
        serviceType: "Glazenwasser",
        name: `Glazenwasser ${location.name}`,
        description: location.intro,
        provider: {
          "@type": "LocalBusiness",
          name: "Cleven Cleaning",
          telephone: "+31683653511",
          url: SITE_URL,
          image: `${SITE_URL}/og-cleven-cleaning.jpg`,
          address: {
            "@type": "PostalAddress",
            addressLocality: "Rosmalen",
            addressRegion: "Noord-Brabant",
            addressCountry: "NL",
          },
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: "4.8",
            reviewCount: "49",
            bestRating: "5",
          },
        },
        areaServed: { "@type": "City", name: location.name },
        offers: {
          "@type": "Offer",
          priceCurrency: "EUR",
          priceSpecification: {
            "@type": "PriceSpecification",
            priceCurrency: "EUR",
            minPrice: "35",
          },
        },
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
          { "@type": "ListItem", position: 2, name: "Werkgebied", item: `${SITE_URL}/#werkgebied` },
          { "@type": "ListItem", position: 3, name: location.title, item: url },
        ],
      },
    ],
  };

  const nearby = location.nearby
    .map((s) => getLocationBySlug(s))
    .filter(Boolean);

  return (
    <>
      <SEO
        title={title}
        description={description}
        canonicalUrl={url}
        jsonLd={jsonLd}
      />

      {/* Location Hero */}
      <section
        id="home"
        className="relative min-h-[75vh] flex items-center justify-center overflow-hidden"
        aria-label={`Glazenwasser ${location.name}`}
      >
        <div className="absolute inset-0">
          <ResponsiveImage
            image={images.hero}
            alt={`Cleven Cleaning glazenwasser aan het werk in ${location.name}, ${location.region}`}
            sizes="100vw"
            loading="eager"
            fetchPriority="high"
            className="absolute inset-0 w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-slate-900/85 via-sky-900/75 to-teal-900/75" />
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-16 text-center">
          <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-md rounded-full px-4 py-2 mb-6 border border-white/20">
            <MapPin className="w-4 h-4 text-sky-400" />
            <span className="text-white/90 text-sm font-medium">
              {location.name} &middot; {location.region}
            </span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            Glazenwasser{" "}
            <span className="bg-gradient-to-r from-sky-400 via-teal-400 to-emerald-400 bg-clip-text text-transparent">
              {location.name}
            </span>
          </h1>

          <p className="text-lg sm:text-xl text-white/85 max-w-2xl mx-auto mb-8">
            {location.intro}
          </p>

          <div className="flex items-center justify-center space-x-2 mb-10">
            <StarRating rating={companyInfo.rating} />
            <span className="text-white font-semibold">{companyInfo.rating}</span>
            <span className="text-white/60">({companyInfo.reviewCount} Google reviews)</span>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button
              asChild
              size="lg"
              className="bg-gradient-to-r from-sky-500 to-teal-500 hover:from-sky-600 hover:to-teal-600 text-white text-lg px-8 py-6 rounded-xl shadow-lg shadow-sky-500/30 hover:scale-105 transition-all"
            >
              <a href="#calculator" data-testid="location-hero-calculator-btn">
                Bereken Direct Uw Prijs
              </a>
            </Button>
            <Button
              asChild
              variant="outline"
              size="lg"
              className="border-2 border-white/30 bg-white/10 backdrop-blur-md text-white hover:bg-white/20 text-lg px-8 py-6 rounded-xl transition-all"
            >
              <a
                href={`tel:${companyInfo.phoneClean}`}
                className="flex items-center space-x-2"
              >
                <Phone className="w-5 h-5" />
                <span>{companyInfo.phone}</span>
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Location Intro / SEO text */}
      <section
        id="over-ons"
        className="py-20 bg-gradient-to-br from-slate-50 to-sky-50"
        aria-labelledby="location-intro-heading"
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2
            id="location-intro-heading"
            className="text-3xl sm:text-4xl font-bold text-slate-800 mb-6"
          >
            Uw vaste glazenwasser in {location.name}
          </h2>
          <div className="space-y-5 text-lg text-slate-700 leading-relaxed">
            <p>{location.intro}</p>
            <p>{location.body}</p>
          </div>

          {/* Neighborhoods */}
          <div className="mt-10">
            <p className="font-semibold text-slate-800 mb-4">
              Wij werken in heel {location.name} &mdash; o.a. in:
            </p>
            <div className="flex flex-wrap gap-3">
              {location.neighborhoods.map((wijk) => (
                <span
                  key={wijk}
                  className="inline-flex items-center gap-2 bg-white rounded-full px-4 py-2 text-slate-700 shadow-sm border border-slate-100"
                >
                  <CheckCircle className="w-4 h-4 text-teal-500" />
                  {wijk}
                </span>
              ))}
            </div>
            <p className="text-sm text-slate-500 mt-4">
              Postcodegebied {location.postcode}. Twijfelt u of uw adres binnen
              ons werkgebied valt? Vul uw postcode in bij de calculator hieronder.
            </p>
          </div>
        </div>
      </section>

      {/* Reused sections */}
      <Services />
      <CalculatorWidget />
      <Reviews />
      <FAQ />

      {/* Nearby locations - internal linking */}
      <section className="py-20 bg-white" aria-labelledby="nearby-heading">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2
            id="nearby-heading"
            className="text-2xl sm:text-3xl font-bold text-slate-800 mb-8 text-center"
          >
            Ook glazenwasser in de buurt van {location.name}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl mx-auto">
            {nearby.map((loc) => (
              <Link
                key={loc.slug}
                to={`/glazenwasser-${loc.slug}`}
                data-testid={`nearby-link-${loc.slug}`}
                className="group flex items-center justify-between gap-3 bg-gradient-to-br from-slate-50 to-sky-50 rounded-2xl p-5 border border-slate-100 hover:border-sky-300 hover:shadow-lg transition-all"
              >
                <span className="font-semibold text-slate-800 group-hover:text-sky-600 transition-colors">
                  Glazenwasser {loc.name}
                </span>
                <ArrowRight className="w-5 h-5 text-slate-400 group-hover:text-sky-500 group-hover:translate-x-1 transition-all" />
              </Link>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link
              to="/"
              data-testid="back-to-home-link"
              className="inline-flex items-center gap-2 text-sky-600 font-semibold hover:text-teal-600 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Terug naar de homepage
            </Link>
          </div>
        </div>
      </section>

      <Contact />
    </>
  );
}
